import { OrderFormat } from '@ancon/wildcat-types'

export const SupportedLanguage = {
  English: 'en',
  Swedish: 'sv',
  Norwegian: 'nb',
}

export const LanguageOptions = [
  {
    label: 'English',
    value: SupportedLanguage.English,
  },
  {
    label: 'Svenska',
    value: SupportedLanguage.Swedish,
  },
  {
    label: 'Norsk',
    value: SupportedLanguage.Norwegian,
  },
]

export const DefaultLanguage = SupportedLanguage.Swedish

export enum PortalHostId {
  Modal = 'ModalsPortal',
  Toast = 'ToastsPortal',
}

export enum AppRoutes {
  Home = '/',
  Outlets = '/outlets',
  Outlet = '/outlet',
  Checkout = '/checkout',
  Auth = '/auth',
  OrderStatus = '/order-status',
  QROrderStatus = '/order-status/qr',
  Account = '/account',
  Orders = '/orders',
  GiftCard = '/giftcard',
  GiftCardPreview = '/giftcard/[giftCardShareKey]',
  GiftCardPurchase = '/giftcard/purchase/[outletSlug]',
  GiftCardPurchasePayment = '/giftcard/purchase/[outletSlug]/checkout/[checkoutId]/payment',
  GiftCardPurchaseStatus = '/giftcard/purchase/[outletSlug]/checkout/[checkoutId]/status',
  GiftCardCheckoutResult = '/giftcard/purchase/[outletSlug]/checkout/result',
  PreOrder = '/pre-order',
  GroupOrder = '/group-order',
  PreOrderLink = '/pre-order/[preOrderId]',
  PreOrderManage = '/pre-order/manage',
  PreOrderStatus = '/pre-order/status',
  PayAndGoGroup = '/pay-and-go/[outletId]/group/[groupId]',
  PayAndGoPayment = '/pay-and-go/[outletId]/check/[checkId]/payment',
  PayAndGoStatus = '/pay-and-go/[outletId]/check/[checkId]/status',
  Wallets = '/wallets',
  WalletDetails = '/wallets/[walletId]',
  WalletsRedirect = '/wallets/redirect',
  Book = '/book',
  Visits = '/visits',
}

export const OrderFormatUrlSlug = {
  none: OrderFormat.None,
  'eat-in': OrderFormat.EatIn,
  'take-away': OrderFormat.TakeAway,
  'table-order': OrderFormat.TableOrder,
  delivery: OrderFormat.Delivery,
  'drive-thru': OrderFormat.DriveThru,
  'box-pickup': OrderFormat.BoxPickup,
}

export const GoogleMapsAPIUrl = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places,geocoding,geometry&callback=Function.prototype`

export const OrderFormatSlugMap: Partial<Record<OrderFormat, string>> = {
  [OrderFormat.None]: 'none',
  [OrderFormat.EatIn]: 'eat-in',
  [OrderFormat.TakeAway]: 'take-away',
  [OrderFormat.TableOrder]: 'table-order',
  [OrderFormat.Delivery]: 'delivery',
  [OrderFormat.DriveThru]: 'drive-thru',
  [OrderFormat.BoxPickup]: 'box-pickup',
}

export const NotAvailable = 'N/A'

export const StockholmCoordinates = {
  latitude: 59.32932349999999,
  longitude: 18.0685808,
}

// TODO: Remove this once BoxPickup is supported for AOA
export const AOWAllOrderFormats = OrderFormat.All | OrderFormat.BoxPickup

export const AppTestIds = {
  Home: {
    OutletSearchInput: 'home-page-outlet-inline-search-input',
  },

  FormInputErrorLabel: 'form-input-error-label-', // + fieldName

  MetaDescription: 'meta-description',

  Toaster: {
    Toast: 'toast-message-', // + message
  },

  // Suffixed with the `ErrorModalType`, so specs can assert *which* error is
  // shown without depending on the translated copy.
  ErrorModal: 'error-modal-', // + errorModalType
  ErrorModalPrimaryButton: 'error-modal-primary-button',
  ActiveOrders: 'active-orders',
  OrderSummaryCard: {
    ViewOrderButton: 'order-summary-card-view-order-button',
  },
  OrderListPage: {
    OrderTabButton: 'order-tab-button',
  },

  OutletSearchResultItem: 'account-outlet-search-result-item-', // + index
  Account: {
    BasicDetails: {
      UserNameValue: 'account-basic-details-user-name-value',
      PhoneNumberValue: 'account-basic-details-phone-number-value',
      EmailValue: 'account-basic-details-email-value',

      EditButton: 'account-basic-details-edit-button',
      QRCodeButton: 'account-basic-details-qr-code-button',
    },
    Addresses: {
      AddAddressButton: 'account-addresses-add-address-button',
      SetAddressButton: 'account-addresses-set-address-button-',
      ConfigureAddressModal: {
        TitleInput: 'account-addresses-configure-address-modal-title-input',
        DeliveryAddress:
          'account-addresses-configure-address-modal-delivery-address',
        AdditionalInstructions:
          'account-addresses-configure-address-modal-additional-instructions',
        SaveButton: 'account-addresses-configure-address-modal-save-button',
        CancelButton: 'account-addresses-configure-address-modal-cancel-button',
      },
      MoreOptionsButton: 'account-addresses-more-options-button',
      DeleteAddressButton:
        'account-addresses-configure-address-modal-delete-address-button',
    },

    EditProfileDetailsModal: {
      CancelButton: 'edit-profile-details-modal-cancel-button',
      SaveButton: 'edit-profile-details-modal-save-button',
    },
  },

  Cart: {
    Container: 'shopping-cart',
    CartButton: 'navbar-cart-button',
    GoToCheckoutButton: 'go-to-checkout-button',
    Item: {
      Container: 'cart-item-container-', // + variantId
      Name: 'cart-item-name',
      Quantity: 'cart-item-quantity',
      TotalPrice: 'cart-item-total-price',
      Modification: 'cart-item-modification',
    },
  },

  OrderGrandTotalAmount: 'order-grand-total-amount',

  NavBar: {
    OutletSearch: 'navbar-outlet-search',

    Dropdown: {
      Option: 'dropdown-option-', // + value
    },

    Button: {
      ProfileName: 'navbar-profile-name-button',
      ProfileDropdown: 'navbar-profile-dropdown-button',
      SignIn: 'navbar-sign-in-button',
      MyVisits: 'navbar-my-visits-button',
    },
  },

  AuthModal: {
    Input: {
      Email: 'auth-modal-email-input',
      Password: 'auth-modal-password-input',
    },
    Button: {
      SignIn: 'auth-modal-sign-in-button',
      NextStep: 'auth-modal-next-step-button',
      SignUp: 'auth-modal-sign-up-button',
      SwitchMode: 'auth-modal-switch-mode-button',
      ResetPassword: 'auth-modal-reset-password-button',
    },
    Checkbox: {
      AgreeToTerms: 'auth-modal-agree-to-terms-checkbox',
    },
  },

  UserInfoFields: {
    FirstName: 'checkout-page-order-contact-first-name',
    LastName: 'checkout-page-order-contact-last-name',
    PhoneNumber: 'checkout-page-order-contact-phone-number',
  },

  Checkout: {
    AddContactInfoButton: 'add-contact-info-button',
    CustomerInfoModalSignInButton: 'order-customer-info-modal-sign-in-button',
    CancelOrderButton: 'checkout-cancel-order-button',
    CancelOrderModalButton: 'checkout-cancel-order-modal-button',
    CancelOrderCountdown: 'checkout-cancel-order-countdown-', // + time
    OrderDiscountButton: 'checkout-order-discount-button',
    OrderDiscountInput: 'checkout-order-discount-input',
    OrderDiscountButtonSubmit: 'checkout-order-discount-button-submit',
    OrderDiscountAppliedInfo: 'checkout-order-discount-applied-info',
    OrderInstructions: {
      Note: 'checkout-order-instructions-note',
      ChangeButton: 'checkout-order-instructions-change-button',
      Input: 'checkout-order-instructions-input',
      SaveButton: 'checkout-order-instructions-save-button',
      CancelButton: 'checkout-order-instructions-cancel-button',
    },
    OrderDiscountError: 'checkout-order-discount-error',
    OrderTableNumberBadge: 'checkout-order-table-number-badge',
    OrderStatusSummary: 'order-status-summary',
    OrderTotalContainer: 'checkout-order-total-container',
    CustomerInfoForm: {
      SubmitButton: 'ContinueButton',
    },
    DeleteOrderButton: 'checkout-delete-order-button',
  },

  deleteOrderModal: {
    DeleteOrderButton: 'delete-order-modal-delete-order-button',
  },

  Order: {
    Modal: {
      OrderFormatButton: 'order-format-button',

      NewOrderTimeConfigureModal: {
        ContinueButton: 'new-order-time-configure-modal-continue-button',
        OrderLaterButton: 'new-order-time-configure-modal-order-later-button',
        Schedule: 'order-configure-time-info-schedule',
        ScheduleOrder: 'order-configure-time-info-schedule-order',
        PickTime: 'order-configure-time-info-time-selection',
        PickDay: 'order-configure-time-info-pick-day',
      },
      ProductDetailsModal: {
        CloseButton: 'product-details-modal-close-button',
      },

      ProductFooterForm: {
        Container: 'product-footer-form-container',
        AddToCartButton: 'product-footer-form-add-to-cart-button',
        TotalPrice: 'product-footer-form-total-price',
        ErrorMessage: 'product-footer-form-error-message',
      },

      ProductUpsellModal: {
        Container: 'product-upsell-modal',
        CloseButton: 'product-upsell-modal-close-button',
        ProductCard: 'product-upsell-product-card-', // + variantId
        ProductCardOutOfStock: 'product-upsell-product-card-out-of-stock-', // + variantId
        ContinueButton: 'product-upsell-modal-continue-button',
      },

      ProductSelection: {
        Container: 'product-selection-container-', // + label
        ContainerBase: 'product-selection-container', // base selector for all containers
        Selectable: 'product-selection-selectable-', // + label
        SelectableBase: 'product-selection-selectable', // base selector for all selectables
      },
    },
  },
  ProductConfigurationAccordionHeader:
    'product-configuration-accordion-header-', // + ProductConfigureAccordionId
  ProductConfigurationAccordion: 'product-configuration-section-', // + section title add-on
  ProductConfigurationAccordionChildren:
    'product-configuration-section-children-', // + section title add-on
  ProductConfigurationAccordionError: 'product-configuration-section-error',

  Outlet: {
    OutletCardListContainer: 'outlet-outlet-card-list-container',
    LoadMoreButton: 'outlet-load-more-button',
    OutletMenu: {
      ProductList: {
        ProductCard: 'outlet-menu-product-list-product-card',
      },
    },

    Menu: {
      ProductList: {
        ProductCard: 'outlet-menu-product-list-product-card',
        OutOfStockTag: 'outlet-menu-product-card-out-of-stock-tag',
      },
    },
  },
  Booking: {
    FinderForm: 'booking-finder-form',
    FinderSubmitButton: 'booking-finder-submit-button',
    FinderResultCard: 'booking-finder-result-card',
    FinderTimeChip: 'booking-finder-time-chip',
    FinderJoinWaitlistButton: 'booking-finder-join-waitlist-button',
    BookTableButton: 'booking-book-table-button',
    TimeOption: 'booking-time-option',
    StepContinueButton: 'booking-step-continue-button',
    ConfirmButton: 'booking-confirm-button',
    VisitCard: 'booking-visit-card',
  },
  OrderDetailsActionItem: {
    ActionButton: 'order-details-action-item-action-button',
  },

  TableOrderConfigureModal: {
    TableNumberInput: 'table-order-configure-modal-table-number-input',
    ContinueButton: 'table-order-configure-modal-continue-button',
  },

  DeliveryLocationPickerModal: {
    ContinueButton: 'delivery-location-picker-modal-continue-button',
  },
}
